import { entity, persistence } from "simpler-state"
import {
  ColumnFiltersState,
  SortingState,
  RowSelectionState,
} from "@tanstack/react-table"

interface ICheckDetailTableState {
  sorting: SortingState
  columnFilters: ColumnFiltersState
  rowSelection: RowSelectionState
  globalFilter: string
}

export const INITIAL_CHECK_DETAIL_TABLE_STATE: ICheckDetailTableState = {
  sorting: [],
  columnFilters: [],
  rowSelection: {},
  globalFilter: "",
}

// Store state as a map keyed by check number
interface ICheckDetailStateMap {
  [checkNumber: string]: ICheckDetailTableState
}

const INITIAL_STATE: ICheckDetailStateMap = {}

function deserializeFn(blob: string): ICheckDetailStateMap {
  try {
    const data = JSON.parse(blob)
    // Ensure each check's state has all required fields
    const result: ICheckDetailStateMap = {}
    for (const [checkNumber, state] of Object.entries(data)) {
      if (typeof state === "object" && state !== null) {
        result[checkNumber] = {
          sorting: (state as any).sorting ?? INITIAL_CHECK_DETAIL_TABLE_STATE.sorting,
          columnFilters: (state as any).columnFilters ?? INITIAL_CHECK_DETAIL_TABLE_STATE.columnFilters,
          rowSelection: (state as any).rowSelection ?? INITIAL_CHECK_DETAIL_TABLE_STATE.rowSelection,
          globalFilter: (state as any).globalFilter ?? INITIAL_CHECK_DETAIL_TABLE_STATE.globalFilter,
        }
      }
    }
    return result
  } catch {
    return INITIAL_STATE
  }
}

const CheckDetailStateEntity = entity(INITIAL_STATE, [
  persistence("check_detail_table_state", { deserializeFn }),
])

export const CheckDetailState = {
  /**
   * Get the table state for a specific check number (reactive)
   */
  use(checkNumber: string): ICheckDetailTableState {
    const stateMap = CheckDetailStateEntity.use()
    return stateMap[checkNumber] ?? INITIAL_CHECK_DETAIL_TABLE_STATE
  },

  /**
   * Get the table state for a specific check number (non-reactive)
   */
  get(checkNumber: string): ICheckDetailTableState {
    const stateMap = CheckDetailStateEntity.get()
    return stateMap[checkNumber] ?? INITIAL_CHECK_DETAIL_TABLE_STATE
  },

  /**
   * Set the table state for a specific check number
   */
  set(checkNumber: string, state: Partial<ICheckDetailTableState>) {
    CheckDetailStateEntity.set(current => {
      const currentState = current[checkNumber] ?? INITIAL_CHECK_DETAIL_TABLE_STATE
      return {
        ...current,
        [checkNumber]: {
          ...currentState,
          ...state,
        },
      }
    })
  },

  /**
   * Update sorting for a specific check number
   */
  setSorting(checkNumber: string, sorting: SortingState) {
    CheckDetailState.set(checkNumber, { sorting })
  },

  /**
   * Update column filters for a specific check number
   */
  setColumnFilters(checkNumber: string, columnFilters: ColumnFiltersState) {
    CheckDetailState.set(checkNumber, { columnFilters })
  },

  /**
   * Update row selection for a specific check number
   */
  setRowSelection(checkNumber: string, rowSelection: RowSelectionState) {
    CheckDetailState.set(checkNumber, { rowSelection })
  },

  /**
   * Update global filter for a specific check number
   */
  setGlobalFilter(checkNumber: string, globalFilter: string) {
    CheckDetailState.set(checkNumber, { globalFilter })
  },

  /**
   * Clear all filters for a specific check number
   */
  clearFilters(checkNumber: string) {
    CheckDetailState.set(checkNumber, {
      columnFilters: [],
      globalFilter: "",
    })
  },
}
